/**
 * Analytic terrain — one seeded height function drives the mesh, the castle
 * plateau, the moat, scatter placement AND the player's feet.
 */
import * as THREE from 'three';
import { mulberry32 } from './textures';

const SEED = 20;

function hash2(x: number, y: number): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ (SEED * 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = x - xi;
  const fy = y - yi;
  const n00 = hash2(xi, yi);
  const n10 = hash2(xi + 1, yi);
  const n01 = 1e-4 + hash2(xi, yi + 1);
  const n11 = hash2(xi + 1, yi + 1);
  return n00 * (1 - fx) * (1 - fy) + n10 * fx * (1 - fy) + n01 * (1 - fx) * fy + n11 * fx * fy;
}

function fbm(x: number, y: number, octaves = 4): number {
  let sum = 0;
  let amp = 0.5;
  let freq = 1;
  for (let i = 0; i < octaves; i += 1) {
    sum += amp * valueNoise(x * freq, y * freq);
    amp *= 0.5;
    freq *= 2.07;
  }
  return sum;
}

export const PLATEAU_H = 9.5;
export const PLATEAU_R = 62;
const PLATEAU_RAMP = 30;
/** Moat cuts a wet ring between the wall base and the outer bank. */
const MOAT_INNER = 46;
const MOAT_OUTER = 57;

/**
 * World height at (x, z). Rolling meadow, a flattened crowned plateau under
 * the castle, and a moat ring carved into its skirt.
 */
export function groundHeight(x: number, z: number): number {
  // Approach-road corridor: damp the meadow noise so the walk south of the
  // gate is a smooth grade, not a noise dip.
  const roadDamp =
    Math.abs(x) < 11 && z > 54 && z < 170
      ? 0.12 + 0.88 * smooth(Math.min(1, (Math.abs(x) - 4) / 7))
      : 1;
  const meadow = ((fbm(x * 0.008 + 9, z * 0.008 + 3) - 0.5) * 14
    + (fbm(x * 0.03, z * 0.03) - 0.5) * 2.4) * roadDamp;
  const r = Math.hypot(x, z);
  // Plateau crown: dead-flat under the castle, blending out over the ramp.
  const t = smooth(Math.min(1, Math.max(0, (r - PLATEAU_R) / PLATEAU_RAMP)));
  let h = (PLATEAU_H + meadow * 0.07) * (1 - t) + meadow * t;
  // Moat: annular dip blended over both.
  const moatT = 1 - smooth(Math.min(1, Math.abs(r - (MOAT_INNER + MOAT_OUTER) * 0.5) /
    ((MOAT_OUTER - MOAT_INNER) * 0.5)));
  h -= moatT * 3.1 * (1 - t * 0.55);
  return h;
}

/** Water level of the moat (sits below the plateau, above the dip floor). */
export function waterLevel(): number {
  return PLATEAU_H - 1.35;
}

/** Slope magnitude — used for rock-vs-grass colouring and placement rules. */
export function groundSlope(x: number, z: number): number {
  const e = 1.2;
  const hx = groundHeight(x + e, z) - groundHeight(x - e, z);
  const hz = groundHeight(x, z + e) - groundHeight(x, z - e);
  return Math.hypot(hx, hz) / (2 * e);
}

export interface TerrainMesh {
  mesh: THREE.Mesh;
  dispose(): void;
}

/** 700×700 m meadow, 220² segments, vertex-coloured by height/slope/noise. */
export function buildTerrain(): TerrainMesh {
  const size = 700;
  const segs = 220;
  const geo = new THREE.PlaneGeometry(size, size, segs, segs);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const rng = mulberry32(31);

  const grass = new THREE.Color('#6f7d42');
  const dry = new THREE.Color('#9a8a54');
  const rock = new THREE.Color('#7b746a');
  const dirt = new THREE.Color('#7c6748');
  const c = new THREE.Color();

  for (let i = 0; i < pos.count; i += 1) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const h = groundHeight(x, z);
    pos.setY(i, h);
    const slope = groundSlope(x, z);
    const n = fbm(x * 0.05, z * 0.05);
    c.copy(grass).lerp(dry, Math.min(1, Math.max(0, (n - 0.32) * 2.4)));
    c.lerp(rock, Math.min(1, Math.max(0, (slope - 0.45) * 1.5)));
    // Dirt road: straight run south from the gate, plus courtyard apron.
    const onRoad =
      (Math.abs(x - 0) < 2.6 && z > MOAT_OUTER && z < 300) ||
      (Math.hypot(x, z) < 44 && Math.abs(z) > 40);
    if (onRoad) c.lerp(dirt, 0.82);
    c.offsetHSL(0, 0, (rng() - 0.5) * 0.05);
    c.multiplyScalar(1.16);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.computeVertexNormals();

  // Subtle detail noise multiplied under the vertex colours — kills the
  // flat low-frequency look up close.
  const detailCanvas = document.createElement('canvas');
  detailCanvas.width = 256;
  detailCanvas.height = 256;
  const dctx = detailCanvas.getContext('2d');
  if (dctx) {
    const drng = mulberry32(91);
    dctx.fillStyle = '#e2e2e2';
    dctx.fillRect(0, 0, 256, 256);
    for (let i = 0; i < 5200; i += 1) {
      const v = Math.round(150 + drng() * 120);
      dctx.fillStyle = `rgba(${v},${v - 6},${v - 14},${0.25 + drng() * 0.3})`;
      dctx.fillRect(drng() * 256, drng() * 256, 1 + drng() * 2.4, 1 + drng() * 2.4);
    }
  }
  const detailTex = new THREE.CanvasTexture(detailCanvas);
  detailTex.colorSpace = THREE.SRGBColorSpace;
  detailTex.wrapS = THREE.RepeatWrapping;
  detailTex.wrapT = THREE.RepeatWrapping;
  detailTex.repeat.set(140, 140);
  detailTex.anisotropy = 4;

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    map: detailTex,
    roughness: 1,
    metalness: 0,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.name = 'castle-terrain';

  return {
    mesh,
    dispose() {
      geo.dispose();
      mat.dispose();
      detailTex.dispose();
    },
  };
}
